import {
  type CatalogUploadPurpose,
  MAX_PRODUCT_IMAGES,
  type ProductDetail,
} from '@repo/api-contracts';
import { type ReactElement, useEffect, useRef, useState } from 'react';
import { useInvalidateProducts } from '@/entities/product';
import { type ResettableRequest, resetSettledRequests } from '@/shared/lib';
import { Button, FileDropField } from '@/shared/ui';
import { type ImageEntry, movedEntry } from '../model/image-entries';
import { useSetImages } from '../model/use-set-images';
import { useImageUpload } from '../upload/use-image-upload';
import { ImageEntryItem } from './image-entry-item';
import { SectionCard } from './section-card';

interface ImageSectionProps {
  readonly productId: string;
  readonly images: ProductDetail['images'];
  readonly detailImage: ProductDetail['detailImage'];
  readonly onSaved: (message: string) => void;
}

/** 대표사진(≤5, 1번이 썸네일) + 상세 이미지(≤1). 저장 시 전체 교체된다. */
export const ImageSection = ({
  productId,
  images,
  detailImage,
  onSaved,
}: ImageSectionProps): ReactElement => {
  const invalidate = useInvalidateProducts();
  const {
    upload,
    isUploading,
    error: uploadError,
    clearError: clearUploadError,
  } = useImageUpload(productId);
  const saveAction = useSetImages({
    onSuccess: () => {
      onSaved('이미지를 저장했습니다.');
      invalidate.detail(productId);
      invalidate.list();
    },
  });
  const uploadRequest: ResettableRequest = { isPending: isUploading, reset: clearUploadError };

  const [entries, setEntries] = useState<readonly ImageEntry[]>(() =>
    images.map((image) => ({ kind: 'saved', key: image.key, url: image.url })),
  );
  const [detailKey, setDetailKey] = useState<string | null>(detailImage?.key ?? null);
  const [detailLocalUrl, setDetailLocalUrl] = useState<string | null>(null);

  // 로컬 미리보기 URL 은 브라우저 메모리를 잡으므로 언마운트 시 해제한다.
  const objectUrlsRef = useRef<string[]>([]);
  useEffect(
    () => () => {
      for (const url of objectUrlsRef.current) {
        URL.revokeObjectURL(url);
      }
    },
    [],
  );

  const createLocalPreview = (file: File): string => {
    const url = URL.createObjectURL(file);
    objectUrlsRef.current.push(url);
    return url;
  };

  // 업로드와 이미지 저장은 오류 한 줄을 함께 쓴다. 요청을 보내기 직전에 다른 쪽의 지난 오류를 지운다 —
  // 아니면 옛 업로드 오류가 뒤의 저장 거부를 가리고, 옛 저장 오류가 뒤의 업로드 성공 뒤에도 남는다.
  // 진행 중인 요청은 건드리지 않는다(resetSettledRequests). 업로드가 사전 검사(형식·크기)에 막히면
  // 그 문구가 오류 한 줄의 앞자리에 올라, 먼저 지운 저장 오류는 어차피 보이지 않는다.
  const uploadFile = (file: File, purpose: CatalogUploadPurpose): Promise<string | null> => {
    resetSettledRequests([saveAction]);
    return upload(file, purpose);
  };

  const saveImages = (): void => {
    resetSettledRequests([uploadRequest]);
    saveAction.setImages({
      id: productId,
      imageKeys: entries.map((entry) => entry.key),
      detailImageKey: detailKey,
    });
  };

  const handleAddImage = async (file: File | undefined): Promise<void> => {
    if (!file || entries.length >= MAX_PRODUCT_IMAGES) {
      return;
    }

    const key = await uploadFile(file, 'PRODUCT_IMAGE');

    if (key) {
      const localUrl = createLocalPreview(file);
      setEntries((current) => [...current, { kind: 'new', key, localUrl }]);
    }
  };

  const handleDetailUpload = async (file: File | undefined): Promise<void> => {
    if (!file) {
      return;
    }

    const key = await uploadFile(file, 'PRODUCT_DETAIL');

    if (key) {
      setDetailKey(key);
      setDetailLocalUrl(createLocalPreview(file));
    }
  };

  const detailPreviewUrl =
    detailImage && detailImage.key === detailKey ? detailImage.url : detailLocalUrl;

  const hasUnsavedChanges =
    entries.some((entry) => entry.kind === 'new') ||
    detailKey !== (detailImage?.key ?? null) ||
    entries.length !== images.length;

  return (
    <SectionCard
      title="이미지"
      description={`대표사진 최대 ${MAX_PRODUCT_IMAGES}장(1번이 썸네일) · 상세 이미지 1장`}
      error={uploadError ?? saveAction.error?.message ?? null}
    >
      <div className="flex flex-col gap-4">
        <div>
          <p className="text-sm font-medium">대표사진</p>
          <ul className="mt-2 flex flex-wrap gap-3">
            {entries.map((entry, index) => (
              <ImageEntryItem
                key={entry.key}
                entry={entry}
                index={index}
                onMove={(from, direction) =>
                  setEntries((current) => movedEntry(current, from, direction))
                }
                onRemove={(key) =>
                  setEntries((current) => current.filter((item) => item.key !== key))
                }
              />
            ))}
          </ul>
          <div className="mt-2">
            <FileDropField
              accept="image/jpeg,image/png,image/webp"
              aria-label="대표사진 추가"
              buttonLabel="대표사진 추가"
              disabled={isUploading || entries.length >= MAX_PRODUCT_IMAGES}
              onFiles={([file]) => void handleAddImage(file)}
            />
          </div>
        </div>

        <div>
          <p className="text-sm font-medium">상세 이미지</p>
          {detailKey ? (
            <div className="mt-2 flex items-center gap-2">
              {detailPreviewUrl ? (
                <img
                  src={detailPreviewUrl}
                  alt="상세 이미지"
                  className="size-20 rounded border object-cover"
                />
              ) : (
                <span className="flex size-20 items-center justify-center rounded border bg-muted text-xs text-muted-foreground">
                  미리보기 없음
                </span>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive"
                onClick={() => {
                  setDetailKey(null);
                  setDetailLocalUrl(null);
                }}
              >
                상세 이미지 제거
              </Button>
            </div>
          ) : (
            <div className="mt-2">
              <FileDropField
                accept="image/jpeg,image/png,image/webp"
                aria-label="상세 이미지 업로드"
                buttonLabel="상세 이미지 선택"
                disabled={isUploading}
                onFiles={([file]) => void handleDetailUpload(file)}
              />
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          <Button size="sm" isLoading={saveAction.isPending} onClick={saveImages}>
            이미지 저장
          </Button>
          {hasUnsavedChanges ? (
            <span className="text-sm text-muted-foreground">저장하지 않은 변경이 있습니다.</span>
          ) : null}
        </div>
      </div>
    </SectionCard>
  );
};
